/**
 * UpdatePrompt - bottom-right toast that surfaces when a new service
 * worker version has been installed and is waiting to activate.
 *
 * Pre-S34 behavior: every deploy auto-skipWaiting'd the new SW. New
 * code took over on the next navigation, but users in long-running
 * tabs (Hours, Capture, Documents) often saw STALE JS for hours
 * because (a) CloudFront's CACHING_OPTIMIZED served stale index.html,
 * and (b) the SW registration never polled for updates.
 *
 * Post-S34 behavior:
 *   1. App.tsx polls registration.update() every 60s.
 *   2. When the SW finds new code, it installs it but does NOT
 *      auto-activate (sw.js no longer calls skipWaiting on install).
 *   3. The new SW sits in `waiting` state.
 *   4. This component detects the waiting SW + surfaces a banner.
 *   5. User clicks Refresh -> we postMessage SKIP_WAITING, then
 *      reload the page once the new SW has activated.
 *
 * Why explicit user action: avoids yanking long-running tabs between
 * code versions mid-action. The user gets to choose when to take the
 * update. Also makes deploy testing easier — operators can verify
 * the prompt actually shows up before declaring a deploy clean.
 */

import { useEffect, useState } from 'react';
import { RefreshCw } from 'lucide-react';

export function UpdatePrompt() {
  const [waitingWorker, setWaitingWorker] = useState<ServiceWorker | null>(null);

  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;

    let cancelled = false;

    const wireRegistration = (reg: ServiceWorkerRegistration) => {
      // If a SW is already waiting at this point, surface immediately.
      if (reg.waiting && navigator.serviceWorker.controller) {
        if (!cancelled) setWaitingWorker(reg.waiting);
      }

      // Listen for future install->wait transitions. Each registration
      // update may discover a newer SW; this handler fires once per
      // discovery, with `installing` populated. We watch its statechange
      // until it lands in `installed` while there's still an active
      // controller (= an old version is in use, so this is an UPDATE,
      // not a fresh install).
      reg.addEventListener('updatefound', () => {
        const newWorker = reg.installing;
        if (!newWorker) return;
        newWorker.addEventListener('statechange', () => {
          if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
            if (!cancelled) setWaitingWorker(newWorker);
          }
        });
      });
    };

    navigator.serviceWorker.getRegistration().then((reg) => {
      if (reg) wireRegistration(reg);
    });

    // controllerchange = new SW activated (because we sent SKIP_WAITING
    // and clients.claim() ran). Reload so the page picks up new chunks.
    const onControllerChange = () => {
      if (cancelled) return;
      window.location.reload();
    };
    navigator.serviceWorker.addEventListener('controllerchange', onControllerChange);

    return () => {
      cancelled = true;
      navigator.serviceWorker.removeEventListener('controllerchange', onControllerChange);
    };
  }, []);

  if (!waitingWorker) return null;

  const handleRefresh = () => {
    // Tell the waiting SW to take over; controllerchange listener will
    // reload the page once it does.
    waitingWorker.postMessage({ type: 'SKIP_WAITING' });
  };

  return (
    <div
      className="fixed bottom-4 right-4 z-50 max-w-sm rounded-lg bg-slate-900 dark:bg-slate-800 text-white shadow-2xl ring-1 ring-white/10"
      role="status"
      aria-live="polite"
    >
      <div className="flex items-center gap-3 px-4 py-3">
        <RefreshCw className="w-4 h-4 shrink-0 text-primary-400" aria-hidden />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium">A new Nexus build is ready.</p>
          <p className="text-xs text-slate-300 mt-0.5">Refresh to load the latest code.</p>
        </div>
        <button
          type="button"
          onClick={handleRefresh}
          className="shrink-0 rounded-md bg-primary-600 hover:bg-primary-500 px-3 py-1.5 text-xs font-medium focus:outline-none focus:ring-2 focus:ring-primary-400 focus:ring-offset-2 focus:ring-offset-slate-900"
        >
          Refresh
        </button>
      </div>
    </div>
  );
}
